/**
 * LLM (Gemini) の「世代」と、世代ごとに有効な generationConfig 項目の正本 (2026-08-30)。
 *
 * 背景: Gemini は世代をまたぐと同じ generationConfig でも扱いが変わる。実機計測:
 *
 * | 項目                             | 第2世代 (gemini-2.5-*) | 第3世代 (gemini-3.*)                       |
 * | -------------------------------- | ---------------------- | ------------------------------------------ |
 * | temperature / topP / topK        | 有効                   | 無視される (エラーにはならない)            |
 * | thinkingConfig.thinkingBudget    | 有効                   | 3.5-flash-lite は HTTP 400、3.7-flash は可 |
 * | thinkingConfig.thinkingLevel     | エラー (非対応)        | 有効 (3.7-flash は minimal → low へ読替)   |
 * | candidateCount >= 2              | 有効                   | 不正引数エラー                             |
 * | maxOutputTokens/responseMimeType | 有効                   | 有効                                       |
 *
 * temperature の実測 (同一プロンプト 6 回生成のユニーク数):
 *   - gemini-2.5-flash      … 0.0 → 2 種 / 2.0 → 6 種 (効いている)
 *   - gemini-3.5-flash-lite … 0.0 → 6 種 / 2.0 → 6 種 (効いていない)
 *   - gemini-3.7-flash      … 0.0 → 6 種 / 2.0 → 6 種 (効いていない)
 *
 * そこで「先に世代を決め、その世代で有効な項目だけを扱う」方式に統一する。世代は
 * `AiGenerationConfig.generation` に明示保存する (未設定の既存データはモデル名から推定)。
 * 明示保存する理由は、候補に無いモデル名を直接入力したときに **管理者の宣言した世代** が
 * そのモデルの扱い (送信する項目) を決められるようにするため。
 *
 * 判定ロジックの正本はこのファイルだけに置く。functions (thinkingConfig.ts / analyzePost)
 * と admin (統合エディタ / プレビュー) が同じ関数を使うことで、実行時の挙動と画面表示が
 * 食い違わないようにしている。
 */
/** 世代の選択肢 (UI の並び順 = 新しい世代が先)。 */
export const AI_MODEL_GENERATIONS = ['v3', 'v2'];
/** 世代の日本語表示名 (管理画面 select / プレビュー共通)。 */
export const AI_MODEL_GENERATION_LABELS = {
    v2: '第2世代 (gemini-2.5 系)',
    v3: '第3世代 (gemini-3 系)',
};
/**
 * 第2世代 (thinkingLevel 非対応 / thinkingBudget 対応 / temperature 有効) と判定するモデル名の前方一致。
 * `gemini-2.` **だけ** を旧系として特別扱いするのは、将来のモデル (4.x 等) が自動的に
 * 第3世代側に入るようにするため。
 */
const LEGACY_GENERATION_MODEL_PREFIX = 'gemini-2.';
/** 前方一致判定用のモデル名正規化 (`models/` 接頭辞と大小文字・前後空白の揺れを吸収)。 */
export function normalizeAiModelName(model) {
    return (model ?? '')
        .trim()
        .toLowerCase()
        .replace(/^models\//, '');
}
/**
 * モデル名から世代を推定する (世代が保存されていない既存データ用)。
 * 未知のモデル名 / 未指定は第3世代扱い (新しい既定モデルが 3 系のため)。
 */
export function inferAiModelGeneration(model) {
    return normalizeAiModelName(model).startsWith(LEGACY_GENERATION_MODEL_PREFIX) ? 'v2' : 'v3';
}
/**
 * 設定から世代を解決する (保存された `generation` を最優先、無ければモデル名から推定)。
 * 実行時 (functions) と画面表示 (admin) の両方がこの 1 関数を通ることで判定を食い違わせない。
 */
export function resolveAiModelGeneration(config) {
    const saved = config?.generation;
    if (saved === 'v2' || saved === 'v3')
        return saved;
    return inferAiModelGeneration(config?.model);
}
/** temperature / topP / topK が効く世代か (第2世代のみ)。 */
export function aiGenerationSupportsSampling(generation) {
    return generation === 'v2';
}
/** thinkingConfig.thinkingBudget が使える世代か (第2世代のみ)。 */
export function aiGenerationSupportsThinkingBudget(generation) {
    return generation === 'v2';
}
/** thinkingConfig.thinkingLevel が使える世代か (第3世代のみ)。 */
export function aiGenerationSupportsThinkingLevel(generation) {
    return generation === 'v3';
}
/**
 * 世代で有効なサンプリング系パラメータだけを返す (API へ送る項目の絞り込み)。
 * 第3世代では API に無視されるだけだが、送る意味が無いうえ将来エラーになりうるため落とす。
 */
export function pickAiSamplingParams(generation, values) {
    if (!aiGenerationSupportsSampling(generation))
        return {};
    return { temperature: values.temperature, topP: values.topP, topK: values.topK };
}
/**
 * 設定を指定世代へ揃える (世代切替 / 保存前の正規化で使う純関数)。
 *
 * - `generation` を明示的に立てる
 * - その世代で使わない項目 (第3世代なら temperature / topP / topK / thinkingBudget、
 *   第2世代なら thinkingLevel) をキーごと削除する
 * - 空になった `thinkingConfig` はオブジェクトごと削除する (空オブジェクトを Firestore に残さない)
 * - `model` / `maxOutputTokens` / `responseMimeType` は世代共通なので保持する
 */
export function applyAiModelGeneration(config, generation) {
    const next = { ...config, generation };
    if (!aiGenerationSupportsSampling(generation)) {
        delete next.temperature;
        delete next.topP;
        delete next.topK;
    }
    if (next.thinkingConfig) {
        const thinking = { ...next.thinkingConfig };
        if (!aiGenerationSupportsThinkingBudget(generation))
            delete thinking.thinkingBudget;
        if (!aiGenerationSupportsThinkingLevel(generation))
            delete thinking.thinkingLevel;
        if (Object.keys(thinking).length > 0) {
            next.thinkingConfig = thinking;
        }
        else {
            delete next.thinkingConfig;
        }
    }
    return next;
}
//# sourceMappingURL=aiModelGeneration.js.map